import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartBarIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useAuditoriaNumeros } from '../../hooks/useAuditoria';
import { fmtNumero } from '../../utils/formatters';
import { preencherDiasZerados } from '../../utils/seriesTelemetria';
import { diasCobertos, deveAvisarCoberturaInsuficiente } from '../../utils/avisosUso';
import { ToggleIncluirAdmin } from './ToggleIncluirAdmin';

const PERIODOS = [7, 30, 90] as const;

const tooltipStyle = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 12,
  color: 'var(--text)',
};

/** "YYYY-MM-DD" -> "DD/MM". Fatiado direto da string — parsear com `new Date`
 *  e reformatar em local sujeitaria a rodar 1 dia pra trás perto da meia-noite. */
const fmtDiaCurto = (iso: string): string => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const fmtDiaCompleto = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const HORAS = Array.from({ length: 24 }, (_, h) => h);

const segBtn = (ativo: boolean) =>
  `rounded-iris-sm px-2.5 py-1 text-xs font-medium transition-colors ${
    ativo ? 'bg-accent text-white' : 'border border-line bg-surface text-tx-2 hover:bg-surface-2'
  }`;

/** Cabe em 168px a 10.5px; o rótulo inteiro continua no tooltip. */
const truncarRotulo = (v: string) => (v.length > 26 ? `${v.slice(0, 25)}…` : v);

/** Aba "Números" — agregados do `GET /api/auditoria/numeros`. Substitui os
 *  antigos KPIs stub (Alterações/Alertas) da aba Eventos com dado real. */
export const AbaNumeros: React.FC = () => {
  const [dias, setDias] = useState<number>(30);
  // Usuário `admin` (a conta do suporte) fora por default — cada atendimento
  // entrava como se fosse operação do cliente e o "usuário mais ativo" virava
  // o suporte.
  const [incluirAdmin, setIncluirAdmin] = useState(false);
  const { data, isLoading, error } = useAuditoriaNumeros(dias, incluirAdmin);

  const total = data?.total ?? 0;
  const semDados = !isLoading && !error && total === 0;

  // Retenção default da auditoria é 60d, mas a janela oferecida vai até 90d —
  // dividir por `dias` cru subestima a média quando parte da janela está fora
  // do histórico guardado (expurgada ou anterior ao 1º evento). `diasCobertos`
  // deriva o denominador certo de `primeiroEvento`, no mesmo molde do aviso
  // "Coletando desde" da aba Uso — não lê `parametros` (esta tela não é ADMIN).
  const diasCobertosVal = diasCobertos(data?.primeiroEvento, dias);
  const mediaPorDia = diasCobertosVal > 0 ? total / diasCobertosVal : 0;
  // Gatilho do banner tem que ser o MESMO corte do denominador da média —
  // `deveAvisarCoberturaInsuficiente` (`diasCobertos(...) < dias`), NÃO
  // `deveAvisarColetaRecente` (`desde < dias`, sem o "+1 inclusive" de
  // `diasCobertos`): esse dispara falso quando o 1º evento cai exatamente no
  // 1º dia da janela (cobertura já é 100%, `diasCobertosVal === dias`). Ver
  // `avisosUso.test.ts` pro caso de borda — o predicado é extraído pra lá de
  // propósito, senão trocar de volta pro antigo aqui não quebraria teste
  // nenhum.
  const coletaRecente = deveAvisarCoberturaInsuficiente(data?.primeiroEvento, dias);

  // `porDia` vem de GROUP BY — dia sem evento não aparece na resposta.
  // Preenche com 0 pra o BarChart não desenhar dias esparsos como barras
  // adjacentes (sugeriria continuidade que não existe).
  const porDiaCompleto = useMemo(
    () => preencherDiasZerados(data?.porDia ?? [], dias),
    [data?.porDia, dias],
  );

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-tx-2">Período</span>
          <div className="flex items-center gap-1" role="radiogroup" aria-label="Janela de dias">
            {PERIODOS.map((p) => (
              <button key={p} type="button" onClick={() => setDias(p)} className={segBtn(dias === p)}>
                {p}d
              </button>
            ))}
          </div>
        </div>
        <ToggleIncluirAdmin valor={incluirAdmin} onChange={setIncluirAdmin} />
      </div>

      {/* Aviso honesto (mesmo molde do "Coletando desde" da aba Uso): a janela
          pedida vaza pra antes do 1º evento (retenção expurgou o resto, ou a
          instalação/coleta é recente) — "Média por dia" já se ajusta sozinha
          (ver `diasCobertosVal`), mas o motivo do ajuste tem que ficar visível,
          senão a média parece só "estranha" sem explicação. */}
      {coletaRecente && data?.primeiroEvento && (
        <div className="flex items-center gap-2 rounded-iris border border-warning-border bg-warning-soft px-3 py-2 text-xs text-warning">
          <InformationCircleIcon className="h-4 w-4 shrink-0" />
          <span>Histórico disponível desde {fmtDiaCompleto(data.primeiroEvento)} — a janela selecionada é maior que os dados guardados.</span>
        </div>
      )}

      {isLoading ? (
        <p className="p-6 text-center text-sm text-tx-3">Carregando números...</p>
      ) : error ? (
        <p className="p-6 text-center text-sm text-danger">Erro ao carregar os números da auditoria.</p>
      ) : semDados ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-iris-lg border border-line bg-surface p-10 text-center text-sm text-tx-3">
          <ChartBarIcon className="h-10 w-10 text-tx-3 opacity-50" />
          <span>Nenhum evento de auditoria nos últimos {dias} dias.</span>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-iris-lg border border-line bg-surface p-4">
              <div className="text-2xl font-bold leading-tight text-tx">{fmtNumero(total)}</div>
              <div className="mt-0.5 text-xs font-medium text-tx-2">Eventos no período ({dias}d)</div>
            </div>
            <div className="rounded-iris-lg border border-line bg-surface p-4">
              <div className="text-2xl font-bold leading-tight text-tx">{fmtNumero(mediaPorDia, 1)}</div>
              <div className="mt-0.5 text-xs font-medium text-tx-2">
                Média por dia {coletaRecente ? `(${diasCobertosVal}d com histórico)` : `(${dias}d)`}
              </div>
            </div>
          </div>

          <div className="rounded-iris-lg border border-line bg-surface p-4">
            <h3 className="mb-2 text-[13px] font-semibold text-tx">Eventos por dia</h3>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={porDiaCompleto} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="dia"
                    tickFormatter={fmtDiaCurto}
                    tick={{ fill: 'var(--text-3)', fontSize: 10.5 }}
                    axisLine={{ stroke: 'var(--border)' }}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: 'var(--text-3)', fontSize: 10.5 }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelStyle={{ color: 'var(--text-2)' }}
                    labelFormatter={(v) => fmtDiaCurto(String(v))}
                    formatter={(v) => [fmtNumero(Number(v)), 'Eventos']}
                    cursor={{ fill: 'var(--surface-2)' }}
                  />
                  <Bar dataKey="contagem" fill="var(--accent)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="rounded-iris-lg border border-line bg-surface p-4">
              <h3 className="mb-2 text-[13px] font-semibold text-tx">Top tipos</h3>
              <div style={{ width: '100%', height: Math.max(160, data!.porTipo.length * 26) }}>
                <ResponsiveContainer>
                  <BarChart data={data!.porTipo} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fill: 'var(--text-3)', fontSize: 10.5 }}
                      axisLine={{ stroke: 'var(--border)' }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="chave"
                      width={168}
                      // Chave longa (conferencia.ocorrencia_registrada) transborda o
                      // eixo e sai cortada pela esquerda. Trunca aqui; o rotulo
                      // inteiro continua no tooltip.
                      tickFormatter={truncarRotulo}
                      tick={{ fill: 'var(--text-3)', fontSize: 10.5 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      labelStyle={{ color: 'var(--text-2)' }}
                      formatter={(v) => [fmtNumero(Number(v)), 'Eventos']}
                      cursor={{ fill: 'var(--surface-2)' }}
                    />
                    <Bar dataKey="contagem" fill="var(--accent)" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-iris-lg border border-line bg-surface p-4">
              <h3 className="mb-2 text-[13px] font-semibold text-tx">Top usuários</h3>
              <div style={{ width: '100%', height: Math.max(160, data!.porUsuario.length * 26) }}>
                <ResponsiveContainer>
                  <BarChart data={data!.porUsuario} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fill: 'var(--text-3)', fontSize: 10.5 }}
                      axisLine={{ stroke: 'var(--border)' }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="chave"
                      width={168}
                      // Chave longa (conferencia.ocorrencia_registrada) transborda o
                      // eixo e sai cortada pela esquerda. Trunca aqui; o rotulo
                      // inteiro continua no tooltip.
                      tickFormatter={truncarRotulo}
                      tick={{ fill: 'var(--text-3)', fontSize: 10.5 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      labelStyle={{ color: 'var(--text-2)' }}
                      formatter={(v) => [fmtNumero(Number(v)), 'Eventos']}
                      cursor={{ fill: 'var(--surface-2)' }}
                    />
                    <Bar dataKey="contagem" fill="var(--accent)" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="rounded-iris-lg border border-line bg-surface p-4">
            <h3 className="text-[13px] font-semibold text-tx">Distribuição por hora</h3>
            {/* ⚠ Legenda obrigatória: o agrupamento é sobre timestamptz sem conversão
                de fuso — mentir sobre isso (ou omitir) é pior do que não ter o gráfico. */}
            <p className="mb-2 text-[11px] text-tx-3">Horário UTC — não convertido pro fuso local.</p>
            <div style={{ width: '100%', height: 180 }}>
              <ResponsiveContainer>
                <BarChart
                  data={HORAS.map((h) => ({ hora: h, contagem: data!.porHora[h] ?? 0 }))}
                  margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="hora"
                    tickFormatter={(h) => String(h).padStart(2, '0')}
                    interval={1}
                    tick={{ fill: 'var(--text-3)', fontSize: 10.5 }}
                    axisLine={{ stroke: 'var(--border)' }}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: 'var(--text-3)', fontSize: 10.5 }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelStyle={{ color: 'var(--text-2)' }}
                    labelFormatter={(h) => `${String(h).padStart(2, '0')}h UTC`}
                    formatter={(v) => [fmtNumero(Number(v)), 'Eventos']}
                    cursor={{ fill: 'var(--surface-2)' }}
                  />
                  <Bar dataKey="contagem" fill="var(--accent)" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AbaNumeros;
